import type { ConsumerState } from '../../state'
import type { AppleGatewayIssueCardRequest, AppleGatewaySaveOfferRequest, PresentedOffer, UserCohort, InterestDetails } from '../../state/appleGateway/apis'
import type { CheckoutPaymentSchedule } from '@afterpaytouch/portal-api/types'
import { PaymentType } from '@afterpay/types'
import { v4 as uuidv4 } from 'uuid'
import type { EnrichedOfferDetail } from './hooks'
import { isValidString, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { formatDateWithTimezone, formatUtcToTimezoneOffset } from '@afterpaytouch/core'
import { Route } from '../../config/router'
import { app, api } from '../../env'

export const parseHasInstoreCard = (value: string | undefined): boolean => {
  return value === 'true'
}

export const getNextApplePaylaterRoute = (hasInstoreCard: boolean): Route => {
  return hasInstoreCard ? Route.CARD_EDUCATION : Route.APPLE_PAYLATER_INITIALISE
}

const APPLE_PAYLATER_TIMEZONE = 'America/Los_Angeles'

const buildSelectedOfferLink = (cohort: UserCohort, token: string): string => {
  if (cohort === 'CASHAPP') {
    return `${app.CASH_COHORT_OFFER_LINK_URL}?client_id=${api.CASH_API_CLIENT_ID}&reference_id=${token}`
  }
  return `${app.AP_COHORT_OFFER_LINK_URL}/${token}`
}

const toEndOfDayUTC = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 23, 59, 59))
}

const formatDateForAppleGateway = (dateString: string): string => {
  const date = toEndOfDayUTC(dateString)
  return formatDateWithTimezone(date, APPLE_PAYLATER_TIMEZONE)
}

/**
 * Builds the saveOffer API request payload for accepted offers
 *
 * @param token - Checkout token
 * @param consumer - Consumer state with user details
 * @param offers - Array of offer details from consumerLending.paymentOptions (mapped to OfferDetail)
 * @param selectedOffer - The offer the user selected
 * @param paymentSchedules - Payment schedules for installment dates and amounts
 * @param userCohort - User cohort (AFTERPAY or CASHAPP)
 * @param expires - Checkout expiration time in UTC ISO8601 format
 * @returns AppleGatewaySaveOfferRequest or null if required data is missing
 */
export const buildSaveOfferRequest = (
  token: string,
  consumer: ConsumerState,
  offers: EnrichedOfferDetail[] | undefined,
  selectedOffer: EnrichedOfferDetail | undefined,
  paymentSchedules: CheckoutPaymentSchedule[] | undefined,
  userCohort: UserCohort,
  expires?: string
): AppleGatewaySaveOfferRequest | null => {
  // Validate required data
  const firstPaymentDueDate = paymentSchedules?.[0]?.installmentDueDate
  const lastPaymentSchedule = paymentSchedules?.[paymentSchedules.length - 1]

  if (
    !consumer.uuid ||
    !consumer.mobile ||
    offers == null ||
    offers.length === 0 ||
    selectedOffer == null ||
    !firstPaymentDueDate ||
    lastPaymentSchedule == null
  ) {
    return null
  }

  // Map offers to presented offers format, filtering out invalid offers
  // Track the selected offer ID as we build the list
  let finalSelectedOfferId: string | null = null

  const presentedOffer: PresentedOffer[] = offers
    .filter((offer) => {
      // Filter to only PBI and PCL offers (Apple Pay Later eligible)
      return (
        (offer.paymentType === PaymentType.PBI || offer.paymentType === PaymentType.PCL) &&
        offer.numberOfInstallments &&
        offer.monthlyAmount &&
        offer.totalAmount &&
        offer.amountDueToday
      )
    })
    .map((offer) => {
      // Generate a unique offer ID
      const offerId = uuidv4()

      // Check if this is the selected offer by comparing the offer properties
      const isSelectedOffer =
        offer.paymentType === selectedOffer.paymentType &&
        offer.numberOfInstallments === selectedOffer.numberOfInstallments &&
        offer.totalAmount.amount === selectedOffer.totalAmount.amount &&
        offer.monthlyAmount.amount === selectedOffer.monthlyAmount.amount

      if (isSelectedOffer) {
        finalSelectedOfferId = offerId
      }

      // Build interest details if APR and interestAmount are provided (for PCL offers)
      const interestDetails: InterestDetails | undefined =
        offer.apr != null && offer.apr !== '' && offer.interestAmount != null
          ? {
              aprInterest: {
                percentage: (parseFloat(offer.apr) / 100).toFixed(4),
                totalInterest: offer.interestAmount,
              },
            }
          : undefined

      return {
        offerId,
        numberOfInstallments: offer.numberOfInstallments,
        recurringInstallmentAmount: offer.monthlyAmount,
        offerType: offer.paymentType as PaymentType.PBI | PaymentType.PCL,
        period: offer.paymentType === PaymentType.PBI ? ('daily' as const) : ('monthly' as const),
        total: offer.totalAmount,
        interval: offer.paymentType === PaymentType.PBI ? 14 : 1, // PBI: bi-weekly (14 days), PCL: monthly (1 month)
        downPayment: {
          amount: offer.amountDueToday.amount,
          currency: offer.amountDueToday.currency,
          dueBy: formatDateForAppleGateway(firstPaymentDueDate),
        },
        firstInstallmentDueDate: formatDateForAppleGateway(firstPaymentDueDate),
        finalInstallmentAmount: {
          amount: lastPaymentSchedule.amount.amount,
          currency: lastPaymentSchedule.amount.currency,
        },
        ...(interestDetails != null && { interestDetails }),
      }
    })

  if (presentedOffer.length === 0) {
    return null
  }

  // If we didn't find the selected offer, fall back to the first one
  if (finalSelectedOfferId === null) {
    finalSelectedOfferId = presentedOffer[0].offerId
  }

  return {
    token,
    userWasDeclined: false,
    userCohort,
    userId: consumer.uuid,
    phoneNumber: consumer.mobile,
    offerDetails: {
      presentedOffer,
      selectedOfferId: finalSelectedOfferId,
      selectedOfferLink: buildSelectedOfferLink(userCohort, token),
      ...(valueIsNotNullOrUndefined(expires) && { validUntil: formatUtcToTimezoneOffset(expires, APPLE_PAYLATER_TIMEZONE) }),
    },
  }
}

/**
 * Builds the issueCard API request payload
 *
 * @param token - Checkout token
 * @param consumer - Consumer state with user details
 * @returns AppleGatewayIssueCardRequest or null if required data is missing
 */
export const buildIssueCardRequest = (token: string, consumer: ConsumerState): AppleGatewayIssueCardRequest | null => {
  // Validate required consumer data
  if (!isValidString(consumer.uuid)) {
    return null
  }

  return {
    token,
    consumer: {
      consumerUuid: consumer.uuid,
    },
  }
}
